// src/utils/vetSimilarityHelper.ts
// Helper functions for checking vet name similarity with Levenshtein distance

import { Vet } from '../services/api';

/**
 * Normalizes a vet name for comparison
 * - Converts to lowercase
 * - Removes extra whitespace
 * - Removes common words like "vet", "veterinary", "clinic", "hospital", "animal"
 * - Removes special characters
 */
const normalizeVetName = (name: string): string => {
  return name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s]/g, '') // Remove special characters (-, &, etc.)
    .replace(/\s+/g, ' ') // Replace multiple spaces with single space
    .replace(/\b(vet|veterinary|clinic|hospital|animal|the|ah)\b/g, '') // Remove common words
    .replace(/\s+/g, ' ') // Clean up spaces again after removal
    .trim();
};

/**
 * Calculate Levenshtein distance between two strings
 * Returns the minimum number of edits (insertions, deletions, substitutions) needed
 */
const levenshteinDistance = (str1: string, str2: string): number => {
  const len1 = str1.length;
  const len2 = str2.length;
  const matrix: number[][] = [];

  // Initialize matrix
  for (let i = 0; i <= len1; i++) {
    matrix[i] = [i];
  }
  for (let j = 0; j <= len2; j++) {
    matrix[0][j] = j;
  }

  // Fill matrix
  for (let i = 1; i <= len1; i++) {
    for (let j = 1; j <= len2; j++) {
      if (str1[i - 1] === str2[j - 1]) {
        matrix[i][j] = matrix[i - 1][j - 1];
      } else {
        matrix[i][j] = Math.min(
          matrix[i - 1][j - 1] + 1, // substitution
          matrix[i][j - 1] + 1, // insertion
          matrix[i - 1][j] + 1 // deletion
        );
      }
    }
  }

  return matrix[len1][len2];
};

/**
 * Calculate similarity percentage between two strings
 * Returns a value between 0 (completely different) and 1 (identical)
 */
const calculateSimilarity = (str1: string, str2: string): number => {
  const distance = levenshteinDistance(str1, str2);
  const maxLength = Math.max(str1.length, str2.length);

  if (maxLength === 0) return 1;

  return 1 - distance / maxLength;
};

/**
 * Enhanced string similarity check
 * Returns true if the normalized names are very similar
 */
const areNamesSimilar = (name1: string, name2: string): boolean => {
  const normalized1 = normalizeVetName(name1);
  const normalized2 = normalizeVetName(name2);

  // Exact match after normalization
  if (normalized1 === normalized2) {
    return true;
  }

  // Skip if either name is too short after normalization
  if (normalized1.length < 3 || normalized2.length < 3) {
    return false;
  }

  // Check if one contains the other
  if (normalized1.includes(normalized2) || normalized2.includes(normalized1)) {
    return true;
  }

  // Calculate similarity using Levenshtein distance
  const similarity = calculateSimilarity(normalized1, normalized2);
  const SIMILARITY_THRESHOLD = 0.7;

  if (similarity >= SIMILARITY_THRESHOLD) {
    return true;
  }

  return false;
};

/**
 * Finds similar vets from the existing vet list
 * Returns array of vets with similar names
 */
export const findSimilarVets = (
  newVetName: string,
  existingVets: Vet[]
): Vet[] => {
  if (!newVetName || newVetName.trim().length < 3) {
    return [];
  }

  return existingVets.filter((vet) => areNamesSimilar(newVetName, vet.name));
};

/**
 * Checks if a vet name is similar to any existing vet
 * Returns the first similar vet found, or null
 */
export const findFirstSimilarVet = (
  newVetName: string,
  existingVets: Vet[]
): Vet | null => {
  const similar = findSimilarVets(newVetName, existingVets);
  return similar.length > 0 ? similar[0] : null;
};
